import { useEffect, useMemo } from "react";
import { Switch, Route, Redirect, Router as WouterRouter } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TranslationsProvider, useTranslations } from "@/lib/translations";
import { BookDemoProvider } from "@/components/BookDemoProvider";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";

// All route pages are imported eagerly. The build prerenders the full <App />
// to HTML per route (true SSG) and the client hydrates it, so every route's
// component must resolve synchronously during render — React.lazy/Suspense
// would emit a fallback on the server and mismatch on hydration. The vendor
// manualChunks split (vite.config.ts) keeps third-party code cacheable.
import Index from "@/pages/Index";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Subscriptions from "@/pages/Subscriptions";
import Companies from "@/pages/Companies";
import CompanyProfile from "@/pages/CompanyProfile";
import CodeOfConduct from "@/pages/CodeOfConduct";
import Methodology from "@/pages/Methodology";
import About from "@/pages/About";
import InvestorSolutions from "@/pages/InvestorSolutions";
import BookDemo from "@/pages/BookDemo";
import BookedDemo from "@/pages/BookedDemo";
import NotFound from "@/pages/not-found";

// Vite injects BASE_URL at build time (`/` by default). Arabic routes live
// under `/ar/<path>` — we detect that prefix once at startup and let wouter
// treat `/ar` as the base, so the same <Route path="/about" /> matches both
// `/about` (English) and `/ar/about` (Arabic) without duplicating routes.
const RAW_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const AR_PREFIX = `${RAW_BASE}/ar`;

function detectLocale(): "en" | "ar" {
  if (typeof window === "undefined") return "en";
  const path = window.location.pathname;
  return path === AR_PREFIX || path.startsWith(`${AR_PREFIX}/`) ? "ar" : "en";
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Index} />
      <Route path="/features">{() => <Redirect to="/" />}</Route>
      <Route path="/contact" component={Contact} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/subscriptions" component={Subscriptions} />
      <Route path="/companies" component={Companies} />
      <Route path="/companies/:slug" component={CompanyProfile} />
      <Route path="/code-of-conduct" component={CodeOfConduct} />
      <Route path="/methodology" component={Methodology} />
      <Route path="/about" component={About} />
      <Route path="/investor-solutions" component={InvestorSolutions} />
      <Route path="/book-demo/:step?" component={BookDemo} />
      <Route path="/bookeddemo" component={BookedDemo} />
      <Route path="/esg-rankings">{() => <Redirect to="/" />}</Route>

      {/* 301-equivalent client redirects from old WordPress URLs to current equivalents.
          Wouter has no native HTTP 301; these redirect on first render so crawlers
          following internal links land on the canonical URL. Hosting-level rewrites
          (e.g. _redirects/vercel.json) should mirror these for true 301 status codes. */}
      <Route path="/blog">{() => <Redirect to="/" />}</Route>
      <Route path="/blog/:rest*">{() => <Redirect to="/" />}</Route>
      <Route path="/author/:rest*">{() => <Redirect to="/" />}</Route>
      <Route path="/wp-content/:rest*">{() => <Redirect to="/" />}</Route>
      <Route path="/wp-admin/:rest*">{() => <Redirect to="/" />}</Route>
      <Route path="/wp-login.php">{() => <Redirect to="/" />}</Route>
      <Route path="/index.php">{() => <Redirect to="/" />}</Route>
      <Route path="/feed">{() => <Redirect to="/" />}</Route>
      <Route path="/category">{() => <Redirect to="/" />}</Route>
      <Route path="/category/:rest*">{() => <Redirect to="/" />}</Route>
      <Route path="/tag">{() => <Redirect to="/" />}</Route>
      <Route path="/tag/:rest*">{() => <Redirect to="/" />}</Route>

      <Route component={NotFound} />
    </Switch>
  );
}

// Keeps <html lang/dir> in sync with the active translation language so
// crawlers, screen readers, and CSS selectors observe a consistent locale.
function HtmlLangSync() {
  const { language, isRTL } = useTranslations();
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [language, isRTL]);
  return null;
}

export interface AppProps {
  // Provided by the build-time prerender (entry-server.tsx). On the client
  // these are undefined and the values are derived from window.location, which
  // matches the prerendered output so hydration stays consistent.
  ssrLocale?: "en" | "ar";
  ssrPath?: string;
}

function App({ ssrLocale, ssrPath }: AppProps = {}) {
  const initialLanguage = useMemo(
    () => ssrLocale ?? detectLocale(),
    [ssrLocale],
  );
  const base = initialLanguage === "ar" ? AR_PREFIX : RAW_BASE;

  return (
    <QueryClientProvider client={queryClient}>
      <TranslationsProvider initialLanguage={initialLanguage}>
        <HtmlLangSync />
        <TooltipProvider>
          <WouterRouter base={base} ssrPath={ssrPath}>
            <BookDemoProvider>
              <Toaster />
              <RouteErrorBoundary>
                <Router />
              </RouteErrorBoundary>
            </BookDemoProvider>
          </WouterRouter>
        </TooltipProvider>
      </TranslationsProvider>
    </QueryClientProvider>
  );
}

export default App;
